if (module.hot) {
  module.hot.accept(() => {
    location.reload()
  })
}

import * as THREE from 'three';

import moment from 'moment';

import markup from './grid-markup';
import vert from '/src/touchpoints/live/lib/glsl/shader.vert';
import frag from './grid.frag';
import fragCutOut from './grid-cutout.frag';
import createTL from './grid-timeline';
import './grid.scss';

import defaultMask from 'url:/src/touchpoints/live/lib/assets/contour-wide.png';
import defaultSwoosh from 'url:/src/touchpoints/live/lib/assets/swoosh.png';

import placedCanvas from '/src/touchpoints/live/lib/js/components/PlacedCanvas/PlacedCanvas';

class Grid {
  constructor (name, datas, onComplete, index, renderers) {
    this.id = `${name}-${index}`;
    this.name = name;
    this.datas = datas;

    this.delayedPlay = false;

    this.onComplete = onComplete;

    const width = window.innerWidth;
    const height = window.innerHeight;

    this.wrapper = document.createElement('div');
    this.wrapper.id = this.id;
    this.wrapper.classList.add('chapter-wrapper');
    this.wrapper.classList.add('ca-grid');
    this.wrapper.classList.add(`is-size-${window.SIZE_ID?.toLowerCase()}`);

    const units = {
      SMALL: '15px',
      MEDIUM: '25px',
      LARGE: '40px',
    }
    document.documentElement.style.setProperty('--lls-ca-grid-unit', units[window.SIZE_ID]);
    const borderThicknesses = {
      SMALL: '1px',
      MEDIUM: '2px',
      LARGE: '2px',
    }
    document.documentElement.style.setProperty('--lls-ca-grid-border-thick', borderThicknesses[window.SIZE_ID]);

    if (width < height) {
      this.isVertical = true;
      this.wrapper.classList.add('is-vertical');
    }
    this.wrapper.innerHTML += markup;
    document.body.appendChild(this.wrapper);

    this.renderer = this.createRenderer('ca-main-canvas', renderers.front);
    this.cutoutRenderer = this.createRenderer('ca-cutout-canvas', renderers.cutout);

    this.scenes = [];

    console.log(this.datas);

    this.insertMediaView(`.content-grid .grid-item-1`, this.datas.assets[0]?.url, 0);
    this.insertMediaView(`.content-grid .grid-item-2`, this.datas.assets[1]?.url, 1);
    this.insertMediaView(`.content-grid .grid-item-3`, this.datas.assets[2]?.url, 2);
    this.insertMediaView(`.freeflow-item`, this.datas.assets[3]?.url, 3, (this.datas.assets[3]?.maskUrl || defaultMask));

    this.loadIndex = 4;

    this.running = false;
  }

  createRenderer (className, renderer) {
    renderer.setClearColor(0xFFFFFF, 0);
    
    this.resizeRendererToDisplaySize(renderer);
    renderer.domElement.classList.add('ca-grid-canvas');
    renderer.domElement.classList.add(className);
    return renderer;
  }

  resizeRendererToDisplaySize (renderer) {
    const canvas = renderer.domElement;
    const pixelRatio = window.devicePixelRatio;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const needResize = canvas.width !== width || canvas.height !== height;
    if (needResize) {
      renderer.setSize(width, height, false);
    }
    return needResize;
  }

  updateTexture (url, index) {
    this.createTexture(url, (texture) => {
      texture.needsUpdate = true;
      this.scenes[index].userData.shader.uniforms.img.value = texture;
      this.scenes[index].userData.shader.uniforms.imgRes.value.x = texture.image.width;
      this.scenes[index].userData.shader.uniforms.imgRes.value.y = texture.image.height;
    });
  }

  onStart () {
  }

  createTexture (url, onLoaded) {
    const img = new Image();
    const texture = new THREE.Texture(img);
    texture.encoding = THREE.sRGBEncoding;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

    img.addEventListener('load', () => {
      onLoaded(texture);
    });

    img.crossOrigin = 'anonymous';
    img.src = url?.replace('.png', '.jpg') || defaultSwoosh;
  }

  insertMediaView (selector, url, index, cutOutURL) {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.createTexture(url, (texture) => {
      texture.needsUpdate = true;

      const cutOutUniforms = cutOutURL
        ? {
          maskPixelAmount: new THREE.Uniform(1),
          contourPixelAmount: new THREE.Uniform(1),
          maskZoom: new THREE.Uniform(0.1),
          cutoutTexture: new THREE.Uniform(new THREE.Texture())
        } : {}

      const scene = placedCanvas({
        parent: this.wrapper.querySelector(selector),
        uniforms: {
          // GENERAL
          seed: (Math.random() * 1000),
          time: new THREE.Uniform(0),
          opacity: new THREE.Uniform(1),
          pixr: new THREE.Uniform(window.devicePixelRatio),
          res: new THREE.Uniform(new THREE.Vector2(width, height)),
          // IMG
          img: new THREE.Uniform(texture),
          offset: new THREE.Uniform(new THREE.Vector2(0)),
          imgRes: new THREE.Uniform(new THREE.Vector2(
            texture.image.width,
            texture.image.height
          )),
          mosaic: new THREE.Uniform(1),
          pixels: new THREE.Uniform(1),
          zoom: new THREE.Uniform(cutOutURL ? 0.8 : 1),
          // CUTOUT (if needed)
          ...cutOutUniforms
        },
        renderer: this.renderer,
        shader: {
          vert,
          frag: cutOutURL ? fragCutOut : frag
        },
      });

      if (cutOutURL) {
        this.createCutOutTex(cutOutURL, scene.userData.shader);
        scene.userData.isCutout = true;
      }
      this.scenes[index] = scene;

      if (this.scenes.filter(s => s).length === this.loadIndex) {
        this.tl = createTL(this.scenes, this.onComplete, this.onStart.bind(this));
        if (this.delayedPlay) {
          this.tl.play(0);
        }
      }
    });
  }

  createCutOutTex (url, shader) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url?.replace('.webp', '.png');
    img.addEventListener('load', (e) => {
      const texture = new THREE.Texture(img);
      texture.encoding = THREE.sRGBEncoding;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.NearestFilter;
      texture.magFilter = THREE.NearestFilter;
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
  
      shader.uniforms.cutoutTexture.value = texture;
      shader.uniforms.cutoutTexture.value.needsUpdate = true;
    });
  }

  renderScene (scene, renderer) {
    const element = scene.userData.parent;
    const rect = element.getBoundingClientRect();

    const width = (rect.right - rect.left);
    const height = (rect.bottom - rect.top);
    const left = rect.left;
    const bottom = (renderer.domElement.clientHeight - rect.bottom);
    const vp = new THREE.Vector4(left, bottom, width, height);

    renderer.setViewport(vp);
    renderer.setScissor(vp);

    scene.userData.shader.uniforms.time.value++;
    scene.userData.shader.uniforms.res.value.x = width;
    scene.userData.shader.uniforms.res.value.y = height;

    renderer.render(scene, scene.userData.camera);
  }

  render () {
    if (this.running) {
      this.renderer.setScissorTest(true);

      this.scenes.forEach((scene) => {
        this.renderScene(scene, scene.userData.isCutout ? this.cutoutRenderer : this.renderer);
      });

      this.renderer.setScissorTest(false);
    }

    this.loop = requestAnimationFrame(this.render.bind(this));
  }

  start () {
    this.wrapper.style.display = 'block';
    if (this.tl) {
      this.tl.play(0);
    } else {
      this.delayedPlay = true;
    }
    this.running = true;
    this.loop = requestAnimationFrame(this.render.bind(this));
  }
  stop () {
    this.wrapper.style.display = 'none';
    this.running = false;
    this.tl?.pause();
    this.tl?.seek(0);
    cancelAnimationFrame(this.loop);
  }
  pause () {
    this.running = false;
    console.log('pause');
    this.tl?.pause();
  }
  resume () {
    this.tl?.resume();
    this.running = true;
  }
}
export default Grid;
